import {
	FunctionComponent,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	UserGroupManagementMode,
	UserGroupOverviewModel,
	UserGroupsPageModel,
	deleteUserGroup,
	getPageViewModel
} from "client/Api/UserGroupsManagement/userGroupsManagement";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPage } from "../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	FloatingActionButton,
	FontIconName,
	IconButton,
	SearchInputForm,
	SearchPlaceholder,
	ToolbarAction,
	ToolbarRegularAction,
	useConfirmationDialog
} from "client/Components";
import { UserGroupOverview, userGroupOverviewSearchPropertyKeys } from "client/Features/Users/UserGroupOverview";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useLocalStorageState,
	useUnmountedCancellation,
	useUserScopedKey,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { EditUserGroupPageParams } from "./EditUserGroupPage";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	connectToRouter,
	route
} from "client/Navigation";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import styles from "./UserGroupManagementPage.module.less";
import { VisualView } from "./VisualView";
import { UserGroupWithRelationsModel } from "./GroupHierarchyGraph";
import { tryParseInt } from "client/Misc/parseUtils";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type UserGroupManagementPageProps = {
	onNavigate: NavigationCallback;
};

export const UserGroupManagementPage = (props: UserGroupManagementPageProps): ReactElement => {
	const {
		onNavigate
	} = props;

	const [model, setModel] = useState<UserGroupsPageModel>({
		userGroups: EMPTY_ARRAY,
		groupRelations: {},
		userGroupManagementMode: UserGroupManagementMode.Edit
	});

	const {
		userGroups: groups,
		groupRelations: relations,
		userGroupManagementMode: mode
	} = model;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [selection, setSelection] = useState<readonly UserGroupOverviewModel[]>([]);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const userScopedKey: string = useUserScopedKey("user-group-mangement:preferred-view");
	const [selectedView, setSelectedView] = useLocalStorageState<View>(
		userScopedKey,
		"table"
	);

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	useActiveSettingsMenuItem(SettingsPageKind.UserGroupManagement);

	const effectiveView: View = isResponsive
		? "table"
		: selectedView;

	useEffect(
		(): void => {
			hasImplicitDependencyOn(effectiveView);
			setSelection([]);
		},
		[effectiveView]
	);

	const groupsWithRelations = useMemo(
		(): readonly UserGroupWithRelationsModel[] => {
			return groups.map(
				(x): UserGroupWithRelationsModel => ({
					id: x.id,
					name: x.name,
					members: relations[x.id] ?? EMPTY_ARRAY
				})
			);
		},
		[groups, relations]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const model: UserGroupsPageModel = await getPageViewModel(signal)
						.catch(handlePageDataLoadingError);

					setModel(model);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const deleteHandler = useCallback(
		async (item: UserGroupOverviewModel): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const result: TryResult<void> = await openConfirmationDialog({
				title: localize("_DeleteUserGroupConfirmationTitle"),
				confirmText: localize("_Ok"),
				children: localize("_DeleteUserGroupConfirmationText"),
				trackingId: "delete-user-group-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				setIsBusy(false);
				return;
			}

			try {
				await deleteUserGroup(item.id, globalCancellation);

				setSelection(
					(x): readonly UserGroupOverviewModel[] => x.filter((g) => g.id !== item.id)
				);

				setModel(
					(x): UserGroupsPageModel => {
						const groupRelations: Record<number, readonly number[] | undefined> = {};

						for (const [key, values] of Object.entries(x.groupRelations)) {
							const keyParseResult: TryResult<number> = tryParseInt(key);

							if (!keyParseResult.wasSuccessful || isNil(values)) {
								continue;
							}

							groupRelations[keyParseResult.result] = values.filter(
								(x) => x !== item.id
							);
						}

						return {
							...x,
							userGroups: x.userGroups
								.filter(g => g.id !== item.id),
							groupRelations
						};
					}
				);

				alertManager.showSuccess(localize("_UserGroupDeletedSuccessfully"));
				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_ErrorOfUserGroupDeletion"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			isBusy,
			openConfirmationDialog
		]
	);

	const createHandler = useCallback(
		(): void => {
			onNavigate(userGroupManagementRouting.create);
		},
		[onNavigate]
	);

	const actionContext = useMemo(
		(): UserGroupManagementActionContext => ({
			mode,
			selection,
			isMobile
		}),
		[
			isMobile,
			mode,
			selection
		]
	);

	const selectionActions = useMemo(
		(): readonly UserGroupManagementAction[] => {
			return getSelectionActions({
				deselectHandler: () => setSelection([])
			});
		},
		[]
	);

	const contextualActions = useMemo(
		(): readonly UserGroupManagementAction[] => {
			return listContextualActions({
				edit: (item) => onNavigate(userGroupManagementRouting.edit({ userGroupId: `${item.id}` })),
				delete: deleteHandler
			});
		},
		[deleteHandler, onNavigate]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<UserGroupManagementActionContext> => {
			return getMainAction(createHandler);
		},
		[createHandler]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly UserGroupManagementAction[] => {
			return buildActionsForTopBar<UserGroupManagementActionContext, UserGroupManagementAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			mainAction,
			selectionActions,
			contextualActions,
			hasSelection,
			isResponsive
		]
	);

	const selectGroupByIdHandler = useCallback(
		(id: number): void => {
			const group: UserGroupOverviewModel | undefined = groups.find(
				(x) => x.id === id
			);

			setSelection(
				group ? [group] : []
			);
		},
		[groups]
	);

	return (
		<SettingsPage
			actions={topBarActions}
			actionContext={actionContext}
			title={localize("_Groups")}
			hasSelection={hasSelection}
			hasBusyIndicator={isBusy}
		>
			<SettingsPage.Section height="stretch">
				<SettingsPage.SearchSection>
					<div className={styles.searchContainer}>
						<SearchInputForm
							onChange={setSearchTerm}
							trackingId="search-term"
							height="large"
							icon="filter"
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={userGroupOverviewSearchPropertyKeys.map(localize)}
								/>
							)}
						/>

						{!isResponsive && (
							<ViewSwitcher
								view={effectiveView}
								onChangeView={setSelectedView}
							/>
						)}
					</div>
				</SettingsPage.SearchSection>

				<SettingsPage.Section height="stretch">
					{effectiveView === "table"
						? (
							<UserGroupOverview
								data={groups}
								searchTerm={searchTerm}
								selection={selection}
								onSelectionChange={(x) => setSelection(x)}
								height="stretch"
							/>
						) : (
							<div className={styles.visualization}>
								<VisualView
									searchTerm={searchTerm}
									selectedGroupId={
										selection.length === 1
											? selection[0]
												.id
											: null
									}
									onChangeSelectedGroupId={selectGroupByIdHandler}
									groups={groupsWithRelations}
								/>
							</div>
						)}
				</SettingsPage.Section>
			</SettingsPage.Section>
			<AiAssistantFabSpacing />
			{mainAction.canExecute(actionContext) && (
				<>
					<div className="preserve-space-for-mobile-plus-button"/>
					<FloatingActionButton
						onClick={() => mainAction.execute(actionContext)}
						icon="plus"
						trackingId="new-button"
					/>
				</>
			)}
			{confirmationDialog}
		</SettingsPage>
	);
};
UserGroupManagementPage.displayName = "UserGroupManagementPage";

type View = "table" | "graph";

const viewToConfigurationMap: Record<View, ViewConfiguration> = {
	["table"]: {
		icon: "bars",
		tooltipKey: "_ListView"
	},
	["graph"]: {
		icon: "chart-line",
		tooltipKey: "_VisualView"
	}
};

const viewToNextViewMap: Record<View, View> = {
	["table"]: "graph",
	["graph"]: "table"
};

type ViewConfiguration = {
	icon: FontIconName;
	tooltipKey: LocalizationKey;
};

type ViewSwitcherProps = {
	view: View;
	onChangeView: (view: View) => void;
};

const ViewSwitcher = (props: ViewSwitcherProps): ReactElement => {
	const {
		view,
		onChangeView
	} = props;

	const nextView: View = viewToNextViewMap[view];

	const { icon, tooltipKey } = viewToConfigurationMap[nextView];

	return (
		<IconButton
			kind="secondary"
			icon={icon}
			size="large"
			tooltip={localize(tooltipKey)}
			trackingId={generateTrackingId("view", `|${nextView}`)}
			onClick={() => onChangeView(nextView)}
		/>
	);
};
ViewSwitcher.displayName = "ViewSwitcher";

export const UserGroupManagementPageForRouter: FunctionComponent = connectToRouter(
	UserGroupManagementPage,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

type UserGroupManagementActionContext = {
	mode: UserGroupManagementMode;
	selection: readonly UserGroupOverviewModel[];
	isMobile: boolean;
};

type UserGroupManagementAction = ToolbarAction<UserGroupManagementActionContext>;

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(
	handlers: SelectionActionsHandlers
): readonly UserGroupManagementAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

type ContextualActionsHandlers = {
	edit: (item: UserGroupOverviewModel) => void;
	delete: (item: UserGroupOverviewModel) => void;
};

function listContextualActions(
	handlers: ContextualActionsHandlers
): readonly UserGroupManagementAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "editUserGroup",
			title: localize("_Edit"),
			icon: "pen",
			canExecute: (c) => c.selection.length === 1,
			execute: (c) => handlers.edit(c.selection[0])
		},
		{
			kind: ActionKind.Regular,
			name: "deleteUserGroup",
			title: localize("_Delete"),
			icon: "trash-can",
			canExecute: (c) => c.mode === UserGroupManagementMode.Edit
				&& c.selection.length === 1,
			execute: (c) => handlers.delete(c.selection[0])
		}
	];
}

function getMainAction(
	newHandler: () => void
): ToolbarRegularAction<UserGroupManagementActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "newUserGroup",
		title: localize("_New"),
		icon: "plus",
		canExecute: (c) => c.mode === UserGroupManagementMode.Edit,
		execute: () => newHandler(),
		isPrimary: true
	};
};

export const userGroupManagementRouting = {
	overview: "/UserGroupsManagement",
	create: "/UserGroupsManagement/Create",
	edit: route<EditUserGroupPageParams>`/UserGroupsManagement/Edit/${"userGroupId"}`
};
